import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { IncomingMove, IncomingTime } from '@/shared/types';
import type { RootState } from '../index';

// Enhanced move type with time data
export interface Move extends IncomingMove {
  id: string; // Unique identifier: `${game_id}-${sequence}`
  sequence: number;
  timestamp: number;
  processed: boolean;
}

interface MovesState {
  byId: { [moveId: string]: Move };
  allIds: string[];
  byGameId: { [gameId: number]: string[] }; // Index by game for fast lookup
  timeData: { [gameId: number]: IncomingTime };
  latestIncomingMove: IncomingMove | null;
  latestIncomingTime: IncomingTime | null;
  processedMoveIds: { [moveId: string]: boolean }; // Use object instead of Set for serialization
}

const initialState: MovesState = {
  byId: {},
  allIds: [],
  byGameId: {},
  timeData: {},
  latestIncomingMove: null,
  latestIncomingTime: null,
  processedMoveIds: {}
};

const movesSlice = createSlice({
  name: 'moves',
  initialState,
  reducers: {
    // Move received from WebSocket
    moveReceived: (state, action: PayloadAction<{ move: IncomingMove; sequence: number }>) => {
      const { move, sequence } = action.payload;
      const moveId = `${move.gameId}-${sequence}`;

      const enhancedMove: Move = {
        ...move,
        id: moveId,
        sequence,
        timestamp: Date.now(),
        processed: false
      };

      state.byId[moveId] = enhancedMove;
      if (!state.allIds.includes(moveId)) {
        state.allIds.push(moveId);
      }

      // Index by game ID
      if (!state.byGameId[move.gameId]) {
        state.byGameId[move.gameId] = [];
      }
      if (!state.byGameId[move.gameId].includes(moveId)) {
        state.byGameId[move.gameId].push(moveId);
      }
      state.latestIncomingMove = move;
    },

    // Time update received
    timeUpdated: (state, action: PayloadAction<{ gameId: number; time: IncomingTime }>) => {
      const { gameId, time } = action.payload;
      const normalizedTime: IncomingTime = { ...time, gameId };
      state.timeData[gameId] = normalizedTime;
      state.latestIncomingTime = normalizedTime;
    },

    clearLatestIncomingMove: (state) => {
      state.latestIncomingMove = null;
    },

    clearLatestIncomingTime: (state) => {
      state.latestIncomingTime = null;
    },

    // Mark move as processed
    markMoveProcessed: (state, action: PayloadAction<string>) => {
      const moveId = action.payload;
      state.processedMoveIds[moveId] = true;

      // Update the move entity
      if (state.byId[moveId]) {
        state.byId[moveId].processed = true;
      }
    },

    // Clear moves for a game
    clearMovesForGame: (state, action: PayloadAction<number>) => {
      const gameId = action.payload;
      const moveIds = state.byGameId[gameId] || [];

      moveIds.forEach(id => {
        delete state.byId[id];
        delete state.processedMoveIds[id];
        const index = state.allIds.indexOf(id);
        if (index > -1) {
          state.allIds.splice(index, 1);
        }
      });

      delete state.byGameId[gameId];
      delete state.timeData[gameId];
    },

    // Clear all moves
    clearAllMoves: (state) => {
      state.byId = {};
      state.allIds = [];
      state.byGameId = {};
      state.timeData = {};
      state.latestIncomingMove = null;
      state.latestIncomingTime = null;
      state.processedMoveIds = {};
    }
  }
});

export const {
  moveReceived,
  timeUpdated,
  clearLatestIncomingMove,
  clearLatestIncomingTime,
  markMoveProcessed,
  clearMovesForGame,
  clearAllMoves
} = movesSlice.actions;

// Selectors
export const selectAllMoves = (state: RootState) =>
  state.moves.allIds.map(id => state.moves.byId[id]);

export const selectMoveById = (state: RootState, moveId: string) =>
  state.moves.byId[moveId];

export const selectMovesForGame = (state: RootState, gameId: number) => {
  const moveIds = state.moves.byGameId[gameId] || [];
  return moveIds.map(id => state.moves.byId[id]).filter(Boolean);
};

export const selectLastMoveForGame = (state: RootState, gameId: number) => {
  const moves = selectMovesForGame(state, gameId);
  return moves.length > 0 ? moves[moves.length - 1] : null;
};

export const selectUnprocessedMovesForGame = (state: RootState, gameId: number) => {
  const moves = selectMovesForGame(state, gameId);
  return moves.filter(move => !move.processed);
};

export const selectTimeForGame = (state: RootState, gameId: number) => state.moves.timeData[gameId];
export const selectLatestIncomingMove = (state: RootState) => state.moves.latestIncomingMove;
export const selectLatestIncomingTime = (state: RootState) => state.moves.latestIncomingTime;

export const selectIsMoveProcessed = (state: RootState, moveId: string) =>
  !!state.moves.processedMoveIds[moveId];

export default movesSlice.reducer;
